"use client"

import { useState, useEffect, useContext } from "react"
import { Box, Grid, Card, CardContent, Typography, Button, Paper, alpha } from "@mui/material"
import { ThemeProvider, createTheme, responsiveFontSizes } from "@mui/material/styles"
import { useTenantNavigation } from "../../hooks/useTenantNavigation"
import { ErpContext } from "../../context/ErpContext"

// Icons
import DashboardIcon from "@mui/icons-material/Dashboard"
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart"
import InventoryIcon from "@mui/icons-material/Inventory"
import PeopleIcon from "@mui/icons-material/People"
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet"
import AssessmentIcon from "@mui/icons-material/Assessment"
import TrendingUpIcon from "@mui/icons-material/TrendingUp"
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import PaymentIcon from "@mui/icons-material/Payment"
import AccessTimeIcon from "@mui/icons-material/AccessTime"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import PaymentsIcon from "@mui/icons-material/Payments"
import PeopleAltIcon from "@mui/icons-material/PeopleAlt"
import ProductionQuantityLimitsIcon from "@mui/icons-material/ProductionQuantityLimits"
import CardTravelIcon from "@mui/icons-material/CardTravel"
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange"
import LocalAtmIcon from "@mui/icons-material/LocalAtm"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import PersonPinIcon from "@mui/icons-material/PersonPin"

// Función para verificar accesos (igual que en NavBar)
const tieneAcceso = (id, accesos) => {
  return accesos?.some((a) => a.idMenuAcceso === id && a.eliminado === false)
}

// Tema personalizado (solo ajustes de diseño y tipografías responsivas)
const baseTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#5b5b5f",
    },
    secondary: {
      main: "#f4b10e",
    },
    success: {
      main: "#4CAF50",
    },
    error: {
      main: "#F44336",
    },
    warning: {
      main: "#FF8042",
    },
    info: {
      main: "#0088FE",
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          transition: "all 0.3s ease",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: "none",
          fontWeight: 600,
        },
      },
    },
  },
})
const theme = responsiveFontSizes(baseTheme)

const HomeComponent = () => {
  const [currentTime, setCurrentTime] = useState(new Date())
  const [userName] = useState("Mauricio") // Esto vendrá del contexto después
  const navigate = useTenantNavigation()
  const { user } = useContext(ErpContext)

  const userRole = user?.rolUsuario
  const userAccesses = user?.accesos || []

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 1000)
    return () => clearInterval(timer)
  }, [])

  const formatTime = (date) => {
    return date.toLocaleTimeString("es-AR", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })
  }

  const formatDate = (date) => {
    return date.toLocaleDateString("es-AR", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    })
  }

  // Accesos Rápidos: Destinos, Grupos de viaje, Clientes, Proveedores, Servicios, Balance, Caja
  const accesosRapidos = [
    {
      id: "INVENTARIO6",
      title: "Destinos",
      description: "Gestión de destinos turísticos",
      icon: <LocationOnIcon sx={{ fontSize: 40 }} />,
      color: theme.palette.info.main,
      path: "/verDestinos",
      allowedRoles: ["Administrador", "Vendedor"],
    },
    {
      id: "INVENTARIO5",
      title: "Grupos de viaje",
      description: "Grupos y paquetes turísticos",
      icon: <CardTravelIcon sx={{ fontSize: 40 }} />,
      color: theme.palette.primary.main,
      path: "/tablaReceta",
      allowedRoles: ["Administrador", "Vendedor"],
    },
    {
      id: "CLIENTES3",
      title: "Clientes",
      description: "Gestión de clientes por grupo",
      icon: <PersonPinIcon sx={{ fontSize: 40 }} />,
      color: theme.palette.success.main,
      path: "/clientePaqueteDetalle",
      allowedRoles: ["Administrador", "Vendedor"],
    },
    {
      id: "COMPRAS4",
      title: "Proveedores",
      description: "Gestión de proveedores",
      icon: <LocalShippingIcon sx={{ fontSize: 40 }} />,
      color: theme.palette.warning.main,
      path: "/gestionProveedoresComponent",
      allowedRoles: ["Administrador"],
    },
    {
      id: "COMPRAS8",
      title: "Servicios",
      description: "Servicios turísticos disponibles",
      icon: <InventoryIcon sx={{ fontSize: 40 }} />,
      color: theme.palette.primary.main,
      path: "/verServicios",
      allowedRoles: ["Administrador", "Vendedor"],
    },
    {
      id: "INVENTARIO8",
      title: "Balance",
      description: "Balance general",
      icon: <AccountBalanceWalletIcon sx={{ fontSize: 40 }} />,
      color: theme.palette.info.main,
      path: "/verBalance",
      allowedRoles: ["Administrador"],
    },
    {
      id: "TRANSACCIONES10",
      title: "Caja",
      description: "Caja y movimientos",
      icon: <LocalAtmIcon sx={{ fontSize: 40 }} />,
      color: theme.palette.success.main,
      path: "/cajaComponent",
      allowedRoles: ["Administrador"],
    },
  ]

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          p: { xs: 1.5, sm: 2 },
          backgroundColor: "#f8f9fa",
          height: "100dvh",
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
        }}
      >
        {/* Header de Bienvenida */}
        <Paper
          elevation={3}
          sx={{
            p: { xs: 1.5, sm: 2, md: 2.5 },
            mb: { xs: 1.5, md: 2 },
            borderRadius: 3,
            backgroundColor: "#1976d2",
            color: "white",
            flexShrink: 0,
          }}
        >
          <Grid container spacing={{ xs: 2, md: 3 }} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box sx={{ display: "flex", alignItems: { xs: "flex-start", md: "center" }, mb: { xs: 1, md: 2 } }}>
                <Box sx={{ textAlign: { xs: "left", md: "left" } }}>
                  <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" } }}>
                    {"¡Bienvenido, "}
                    {user?.nombre || "Usuario"}
                    {"!"}
                  </Typography>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontSize: { xs: "0.95rem", sm: "1rem", md: "1.1rem" }, opacity: 0.95 }}
                  >
                    {"Rol: "}
                    {user?.rol || "Usuario"}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: { xs: "left", md: "right" } }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: { xs: "flex-start", md: "flex-end" },
                    mb: 1,
                  }}
                >
                  <CalendarTodayIcon sx={{ mr: 1, fontSize: { xs: 18, sm: 20 } }} />
                  <Typography
                    variant="body1"
                    sx={{ fontSize: { xs: "0.9rem", sm: "1rem" }, textTransform: "capitalize" }}
                  >
                    {formatDate(currentTime)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: { xs: "flex-start", md: "flex-end" },
                  }}
                >
                  <AccessTimeIcon sx={{ mr: 1, fontSize: { xs: 18, sm: 20 } }} />
                  <Typography variant="h6" sx={{ fontFamily: "monospace", fontSize: { xs: "1rem", sm: "1.1rem" } }}>
                    {formatTime(currentTime)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Accesos Rápidos (fijo, sin scroll) */}
        <Paper
          elevation={2}
          sx={{
            p: { xs: 1.5, sm: 2 },
            borderRadius: 3,
            flex: "1 1 auto",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              display: "flex",
              alignItems: "center",
              mb: { xs: 1.5, md: 2 },
              fontSize: { xs: "1rem", sm: "1.1rem" },
            }}
          >
            <DashboardIcon sx={{ mr: 2, color: theme.palette.secondary.main, fontSize: { xs: 20, sm: 24 } }} />
            {"Accesos Rápidos"}
          </Typography>
          <Grid container spacing={{ xs: 1.5, sm: 2, md: 2 }} sx={{ flex: 1, alignContent: "flex-start" }}>
            {accesosRapidos.map((section, idx) => {
              const hasAccess = tieneAcceso(section.id, userAccesses)
              return (
                <Grid item xs={6} sm={6} md={3} key={idx}>
                  <Card
                    onClick={() => hasAccess && navigate(section.path)}
                    sx={{
                      height: "100%",
                      cursor: hasAccess ? "pointer" : "default",
                      opacity: hasAccess ? 1 : 0.5,
                      pointerEvents: hasAccess ? "auto" : "none",
                      border: `2px solid transparent`,
                      "&:hover": hasAccess ? { borderColor: section.color } : {},
                    }}
                  >
                    <CardContent
                      sx={{
                        p: { xs: 1, sm: 1.25 },
                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: 44, sm: 48, md: 52 },
                          height: { xs: 44, sm: 48, md: 52 },
                          borderRadius: "50%",
                          backgroundColor: alpha(section.color, 0.1),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mx: "auto",
                          mb: 1,
                          color: section.color,
                          "& svg": {
                            fontSize: { xs: 22, sm: 26, md: 28 },
                          },
                        }}
                      >
                        {section.icon}
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          color: section.color,
                          fontWeight: 600,
                          fontSize: { xs: "0.8rem", md: "0.9rem" },
                          mb: 0.25,
                        }}
                      >
                        {section.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mt: 0.25,
                          fontSize: { xs: "0.7rem", sm: "0.75rem" },
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {section.description}
                      </Typography>
                      <Button
                        variant="contained"
                        fullWidth
                        disabled={!hasAccess}
                        sx={{
                          mt: 1,
                          backgroundColor: section.color,
                          py: { xs: 0.5, sm: 0.6 },
                          fontSize: { xs: "0.75rem", sm: "0.8rem" },
                          pointerEvents: hasAccess ? "auto" : "none",
                          "&:hover": { backgroundColor: hasAccess ? alpha(section.color, 0.8) : undefined },
                        }}
                      >
                        {"Acceder"}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              )
            })}
          </Grid>
        </Paper>
      </Box>
    </ThemeProvider>
  )
}

export default HomeComponent
